/**
 * SIL-2335 - the stage `errorReason` vocabulary as TypeScript constants.
 *
 * `stage-reasons.json` (this directory) is the SOURCE: the bench agent `include_str!`s it. TS does NOT import the JSON, because
 * a JSON import needs an import attribute under plain Node ESM and this module is reachable from the shared root barrel, which
 * images such as passport-render load as dist on plain Node. The arrays below are therefore checked in, and
 * __tests__/stage-reasons.test.ts reads the JSON and asserts deep equality, so they cannot drift.
 */

/** Reasons for a stage that never started (tool or GPU pack missing/refused, or server-gated). Not run, never a failure. */
export const NOT_RUN_REASON_LIST: readonly string[] = [
  'tool_unavailable',
  'gpu_pack_unavailable',
  'gpu_pack_download_failed',
  'gpu_pack_hash_mismatch',
  'gpu_module_refused',
  'gpu_init_failed',
  'gpu_userspace_hash_mismatch',
  'gpu_pack_signature_invalid',
  'gpu_pack_source_refused',
  'gpu_argv_refused',
  'gpu_modules_locked',
  'gpu_pack_unconfirmed',
];

/** Reasons only the server assigns; an agent that sends one is refused. Every one is also a not-run reason. */
export const SERVER_ASSIGNED_REASON_LIST: readonly string[] = [
  'gpu_pack_unconfirmed',
];

/**
 * Reasons the agent's wipe gate gives for NOT starting a destructive stage (apps/bench-agent/src/run.rs `GateRefusal` and
 * `run_one`). TS-only: the agent does not need the list and the database does not read it. Stored with status 'error', but nothing
 * ran. Tool failures (`fail`, `timeout`, an `error` from the tool itself failing to start) are not here.
 */
/**
 * SIL-2370: wipe results that are INCONCLUSIVE, not failed. The ladder ran, nothing survived, but the erase is not proven (the
 * media deallocated so every marker reads zeros, or the sanitize log did not confirm it). The stage carries a wipe ticket and
 * status 'error'; it grades as not run (never failed, never defective) whatever the ticket, and the erase record says
 * `unconfirmed` so the data status stays "sanitization required". A survivor or an unreadable block is NOT here: that is a fail.
 */
export const WIPE_INCONCLUSIVE_REASONS: ReadonlySet<string> = new Set(['wipe_unconfirmed']);

export const WIPE_GATE_REASONS: ReadonlySet<string> = new Set([
  'authorization_timeout',
  'run_not_running',
  'identity_mismatch',
  'partition_mounted',
  'not_authorized',
  'unsupported_destructive_kind',
  'no_device_path',
  'identity_not_unique',
  'session_expired',
  // The agent's own gate arm when /wipes/ticket answers without segment_claims (run.rs wipe_gate_in_segment).
  'segment_claims_unconfirmed',
  // SIL-2338: the ticket route's 409 codes, passed through verbatim by the agent (run.rs error_code filter). Nothing wiped.
  'segment_unclaimed',
  'run_expired',
  'segment_not_active',
  'wrong_segment',
  'wrong_environment',
  'not_last_segment',
  'attach_not_supported',
  'rediscovery_missing',
  'identity_conflict',
  'ticket_open',
  // SIL-2370: a NVME_WIPE_LADDER stage the agent refused before asking for a ticket (not an NVMe controller path). Nothing wiped.
  // The ladder's own failure reasons (wipe_unsupported, wipe_partial, wipe_readback_failed, ...) are NOT here: they post with a
  // ticket after the ladder ran, so they grade as a failed stage (the drive still holds data), never as not run.
  'wipe_ladder_device_refused',
]);
